import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  LinearProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import LogoutIcon from '@mui/icons-material/Logout';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const Admin = () => {
  const [logos, setLogos] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const navigate = useNavigate();

  const logoInputRef = useRef(null);
  const excelInputRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const handleLogoChange = (e) => {
    const files = Array.from(e.target.files);
    // Validate file sizes
    const oversized = files.filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      setError(`${oversized.length} logo file(s) exceed ${MAX_FILE_SIZE_MB}MB limit: ${oversized.map(f => f.name).join(', ')}`);
      e.target.value = '';
      return;
    }
    setError('');
    setErrorDetails(null);
    setLogos(files);
  };

  const handleExcelChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setError(`Excel file exceeds ${MAX_FILE_SIZE_MB}MB limit (${(file.size / (1024 * 1024)).toFixed(1)}MB)`);
      e.target.value = '';
      return;
    }
    setError('');
    setErrorDetails(null);
    setExcelFile(file);
  };

  const parseErrorFromBlob = async (error) => {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        return json;
      } catch {
        return { error: 'Error generating certificates' };
      }
    }
    return {
      error: error.response?.data?.error || error.message || 'Error generating certificates'
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorDetails(null);
    setSuccess('');
    setLoading(true);
    setUploadProgress(0);
    setProcessingStage('Uploading files...');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const formData = new FormData();
      logos.forEach((logo) => {
        formData.append('logos', logo);
      });
      if (excelFile) {
        formData.append('excel', excelFile);
      }

      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob',
        timeout: 300000,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
          if (percent >= 100) {
            setProcessingStage('Processing certificates... This may take a moment.');
          }
        }
      });

      if (response.status === 200) {
        const certCount = response.headers['x-certificate-count'] || '';

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'certificates.zip');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setSuccess(`${certCount ? certCount + ' certificates' : 'Certificates'} generated successfully! ZIP downloaded.`);
        setLogos([]);
        setExcelFile(null);
        if (logoInputRef.current) logoInputRef.current.value = '';
        if (excelInputRef.current) excelInputRef.current.value = '';
      } else {
        throw new Error('Failed to generate certificates');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        navigate('/login');
        return;
      }

      const parsed = await parseErrorFromBlob(err);
      setError(parsed.error || 'Error generating certificates');
      if (parsed.details) {
        setErrorDetails(parsed.details);
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setProcessingStage('');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      <Container maxWidth="md">
        <Paper
          className="glass-card"
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative top gradient bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)',
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, mt: 1 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                Certificate Generator
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Upload partner logos and student data to generate QR certificates.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ borderRadius: 2, display: { xs: 'none', sm: 'flex' } }}
            >
              Logout
            </Button>
            <IconButton color="error" onClick={handleLogout} sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <LogoutIcon />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              {/* Logos Upload */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'transparent',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(59, 130, 246, 0.04)' }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <ImageIcon sx={{ fontSize: 40, color: 'primary.light' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Partner Logos
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload PNG or JPG images (Max 10 files)
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    disabled={loading}
                    startIcon={<CloudUploadIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Select Logos
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={handleLogoChange}
                      ref={logoInputRef}
                    />
                  </Button>
                  {logos.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={`${logos.length} file(s) selected`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Excel Upload */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'transparent',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'secondary.main', bgcolor: 'rgba(139, 92, 246, 0.04)' }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <InsertDriveFileIcon sx={{ fontSize: 40, color: 'secondary.light' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Student Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload Excel file (.xlsx or .xls)
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    component="label"
                    disabled={loading}
                    startIcon={<CloudUploadIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Select Excel File
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      hidden
                      onChange={handleExcelChange}
                      ref={excelInputRef}
                    />
                  </Button>
                  {excelFile && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={excelFile.name}
                        color="secondary"
                        variant="outlined"
                        size="small"
                        sx={{ maxWidth: '100%' }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Progress Section */}
              {loading && (
                <Grid item xs={12}>
                  <Box sx={{ width: '100%', p: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {processingStage}
                      </Typography>
                      {uploadProgress < 100 && (
                        <Typography variant="body2" color="primary" fontWeight={600}>
                          {uploadProgress}%
                        </Typography>
                      )}
                    </Box>
                    <LinearProgress
                      variant={uploadProgress >= 100 ? 'indeterminate' : 'determinate'}
                      value={uploadProgress}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </Grid>
              )}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || logos.length === 0 || !excelFile}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    background: (loading || logos.length === 0 || !excelFile) 
                      ? undefined 
                      : 'linear-gradient(45deg, #3B82F6 30%, #8B5CF6 90%)',
                    color: 'white',
                    border: 0,
                  }}
                >
                  {loading ? 'Generating...' : 'Generate Certificates'}
                </Button>
              </Grid>
            </Grid>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {error}
              </Typography>
              {errorDetails && errorDetails.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {errorDetails.map((detail, i) => (
                    <Chip
                      key={i}
                      label={detail}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Admin;